import type { QueryClient } from '@tanstack/react-query';
import type { RemovalGap } from '@/components/signoff/RemovalCoverageGaps';
import type { PostLockWarning } from '@/lib/lockCycleDisplay';
import type { RemovalRunResolutionEntry } from '@/lib/removalRunResolutionMessages';
import API_BASE from '@/lib/apiBase';
import { getAccessToken } from '@/lib/authBootstrap';
import { attachTransientMetadata, parseRetryAfterHeader } from '@/lib/transientApiErrors';
import { featureFlag } from '@/utils/featureFlag';

export function isTaskDistributionBootstrapEnabled(): boolean {
  return featureFlag('TASK_DISTRIBUTION_BOOTSTRAP');
}

export type TaskDistributionBootstrapFilters = {
  retailers: Array<{ id: number; name: string }>;
  bookingCycles: Array<{
    id: number;
    retailerId: number;
    name: string;
    startDate: string | null;
    endDate: string | null;
    locked_flag: boolean;
  }>;
  stores: Array<{ id: number; name: string; retailerId: number }>;
  operators: Array<{ id: number; name: string }>;
  mediaFormats: Array<{ id: number; name: string }>;
  campaigns: Array<{ id: number; name: string; retailerId?: number | null }>;
};

export type TaskDistributionBootstrapPayload = {
  meta: {
    cycleId: number;
    cycleName: string;
    retailerId: number;
    lockedAt: string | null;
    lockedBy: number | null;
    lockedByName: string | null;
    removalGenerationMode: string | null;
    dataAsOf: string;
  };
  lock: {
    id: number;
    version: number;
    postLockWarnings?: PostLockWarning[];
  };
  assignments: Record<string, unknown>[];
  removalGaps: RemovalGap[];
  removalRunResolution: RemovalRunResolutionEntry[];
  removalOperatorMismatches?: import('@shared/task-distribution/removalOperatorMismatch').RemovalOperatorMismatch[];
  filters: TaskDistributionBootstrapFilters;
  mobile: {
    assignmentsSummary: Record<string, unknown>[];
    storeSendStatus: Record<string, unknown>[];
  };
  removalBlocked: boolean;
  removalBlockError: string | null;
  removalBlockMessage: string | null;
};

export class TaskDistributionBootstrapError extends Error {
  status: number;
  retryAfterMs?: number;

  constructor(message: string, status: number, retryAfterMs?: number) {
    super(message);
    this.name = 'TaskDistributionBootstrapError';
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    attachTransientMetadata(this, status, retryAfterMs);
  }
}

export function taskDistributionQueryKey(cycleId: string | number | null | undefined) {
  return ['/api/locked-cycles', cycleId, 'task-distribution'] as const;
}

export async function fetchTaskDistributionBootstrap(
  cycleId: string | number,
): Promise<TaskDistributionBootstrapPayload> {
  const token = await getAccessToken();
  const headers: HeadersInit = { Accept: 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/api/locked-cycles/${cycleId}/task-distribution`, {
    method: 'GET',
    headers,
    credentials: 'omit',
  });

  let body: Record<string, unknown> = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }

  if (res.status === 404) {
    throw new TaskDistributionBootstrapError(
      typeof body.message === 'string' ? body.message : 'Locked cycle not found',
      404,
    );
  }

  if (res.status === 409) {
    return {
      meta: body.meta as TaskDistributionBootstrapPayload['meta'],
      lock: body.lock as TaskDistributionBootstrapPayload['lock'],
      assignments: Array.isArray(body.assignments) ? body.assignments : [],
      removalGaps: Array.isArray(body.removalGaps) ? body.removalGaps : [],
      removalRunResolution: Array.isArray(body.removalRunResolution) ? body.removalRunResolution as RemovalRunResolutionEntry[] : [],
      removalOperatorMismatches: Array.isArray(body.removalOperatorMismatches) ? body.removalOperatorMismatches : [],
      filters: body.filters as TaskDistributionBootstrapFilters,
      mobile: body.mobile as TaskDistributionBootstrapPayload['mobile'],
      removalBlocked: true,
      removalBlockError: typeof body.error === 'string' ? body.error : 'Removal run selection blocked',
      removalBlockMessage:
        typeof body.message === 'string'
          ? body.message
          : 'Task Distribution could not safely resolve removal runs for this active cycle',
    };
  }

  if (!res.ok) {
    const retryAfterMs = parseRetryAfterHeader(res.headers.get('Retry-After'));
    throw new TaskDistributionBootstrapError(
      typeof body.error === 'string' ? body.error : `Bootstrap failed (${res.status})`,
      res.status,
      retryAfterMs,
    );
  }

  return {
    meta: body.meta as TaskDistributionBootstrapPayload['meta'],
    lock: body.lock as TaskDistributionBootstrapPayload['lock'],
    assignments: Array.isArray(body.assignments) ? body.assignments : [],
    removalGaps: Array.isArray(body.removalGaps) ? body.removalGaps : [],
    removalRunResolution: Array.isArray(body.removalRunResolution) ? body.removalRunResolution as RemovalRunResolutionEntry[] : [],
    removalOperatorMismatches: Array.isArray(body.removalOperatorMismatches) ? body.removalOperatorMismatches : [],
    filters: body.filters as TaskDistributionBootstrapFilters,
    mobile: body.mobile as TaskDistributionBootstrapPayload['mobile'],
    removalBlocked: false,
    removalBlockError: null,
    removalBlockMessage: null,
  };
}

export function invalidateTaskDistributionQueries(
  queryClient: QueryClient,
  cycleId: string | null | undefined,
) {
  if (!cycleId) return;
  // Prefix covers task-distribution and signoff-v2-data under the same cycle.
  queryClient.invalidateQueries({ queryKey: ['/api/locked-cycles', cycleId] });
  // Legacy mobile sent-status queries (no-ops when disabled under bootstrap flag).
  queryClient.invalidateQueries({ queryKey: ['mobile-assignments-summary', cycleId] });
  queryClient.invalidateQueries({ queryKey: ['mobile-assignments-stores', cycleId] });
}
